import { io, Socket } from 'socket.io-client';
import type { ChatMessage } from '@/api/chat.api';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const CHAT_NAMESPACE = '/chat';

/** Mirror of `ChatEvent` in the API's `chat/chat.types.ts`. */
export interface ChatSocketEvents {
  'chat:message': { message: ChatMessage };
  'chat:message.deleted': { conversationId: string; messageId: string };
  'chat:message.updated': { message: ChatMessage };
  'chat:read': { conversationId: string; staffId: string; lastReadAt: string };
  'chat:typing': { conversationId: string; staffId: string; isTyping: boolean };
  'chat:conversation': { conversationId: string };
  'chat:conversation.removed': { conversationId: string };
  'chat:presence': { staffId: string; online: boolean };
  /** Only to the person who changed it; the setting is theirs alone. */
  'chat:mute': {
    conversationId: string;
    muted: boolean;
    mutedUntil: string | null;
  };
}

type EventName = keyof ChatSocketEvents;
type Listener<E extends EventName> = (payload: ChatSocketEvents[E]) => void;

const EVENTS: EventName[] = [
  'chat:message',
  'chat:message.deleted',
  'chat:message.updated',
  'chat:read',
  'chat:typing',
  'chat:conversation',
  'chat:conversation.removed',
  'chat:presence',
  'chat:mute',
];

/** How long to wait before presenting a refreshed cookie after a rejection. */
const UNAUTHORIZED_RETRY_MS = 30_000;

/**
 * The staff chat socket.
 *
 * Built like `realtimeService`: one socket for the whole app, opened by
 * `ChatProvider` once the user has a clinic and may use chat, closed on
 * logout. The server reads who it is from the session cookie, so nothing
 * here names a user, a clinic or a room.
 *
 * ── Why listeners live here and not on the socket ─────────────────────────
 *
 * Components subscribe to this service, never to the `Socket`. The socket is
 * replaced on a clinic switch and after a rejected token; listeners attached
 * to the old one would be lost, and the screens that attached them would be
 * holding a dead object. The earlier version handed the raw socket to every
 * panel, each of which added and removed its own handlers on every render of
 * a new conversation, which is how the memory investigation ended with chat
 * switched off. Here the socket has exactly one handler per event for its
 * whole life.
 *
 * Messages are sent over HTTP. The only thing this socket sends is "typing".
 */
class ChatSocketService {
  private socket: Socket | null = null;
  private listeners = new Map<EventName, Set<Listener<EventName>>>();
  private statusListeners = new Set<() => void>();
  private reconnectListeners = new Set<() => void>();
  private wasConnected = false;
  private wanted = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    this.wanted = true;
    if (this.socket) return;

    this.socket = io(`${SOCKET_URL}${CHAT_NAMESPACE}`, {
      // Websocket only: a polling session is several HTTP requests that must
      // all reach the same API process, and the API runs several.
      transports: ['websocket'],
      withCredentials: true,
      // Its own connection, so the notification socket's give-up-after-five
      // reconnect policy is not this one's too.
      forceNew: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15_000,
      reconnectionAttempts: Infinity,
      timeout: 20_000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      // The first connect is not a reconnect: nothing was missed yet.
      if (this.wasConnected) {
        for (const listener of this.reconnectListeners) listener();
      }
      this.wasConnected = true;
      this.notifyStatus();
    });
    this.socket.on('disconnect', () => this.notifyStatus());
    this.socket.on('connect_error', () => this.notifyStatus());

    // The cookie was rejected: expired while the tab slept, or the role has
    // no chat access. Stop socket.io retrying the same cookie every second
    // and try once more later, after the HTTP client has refreshed it.
    this.socket.on('chat:unauthorized', () => {
      this.teardown();
      if (!this.wanted) return;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        if (this.wanted) this.connect();
      }, UNAUTHORIZED_RETRY_MS);
    });

    for (const event of EVENTS) {
      this.socket.on(event, (payload: ChatSocketEvents[typeof event]) => {
        const set = this.listeners.get(event);
        if (!set) return;
        for (const listener of set) {
          try {
            listener(payload);
          } catch (error) {
            console.error(`Chat listener for ${event} failed:`, error);
          }
        }
      });
    }
  }

  disconnect(): void {
    this.wanted = false;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.teardown();
  }

  /** Drop and re-open the socket, so a new session cookie is presented. */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  private teardown(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.wasConnected = false;
    this.notifyStatus();
  }

  on<E extends EventName>(event: E, listener: Listener<E>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener<EventName>);
    return () => {
      set.delete(listener as Listener<EventName>);
    };
  }

  /** Fires after the socket comes back, when whatever was sent meanwhile was missed. */
  onReconnect(listener: () => void): () => void {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  onStatusChange(listener: () => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  sendTyping(conversationId: string, isTyping: boolean): void {
    if (!this.socket?.connected) return;
    this.socket.emit('chat:typing', { conversationId, isTyping });
  }

  private notifyStatus() {
    for (const listener of this.statusListeners) listener();
  }
}

export const chatSocketService = new ChatSocketService();
